<template>
  <div class="body edit-panel flex flex-col gap-4">
    <div
      v-if="!isPollLoaded"
      class="poll-editor-empty"
      data-test="poll-editor-empty"
    >
      <p class="poll-editor-empty__message">
        {{ isLoadingPoll ? 'Loading poll…' : 'This poll is unavailable. It may have been deleted, or failed to load.' }}
      </p>
    </div>

    <template v-else>
      <div class="tb-form-group">
      <label class="tb-label">Layout Title</label>

      <input
        class="tb-input"
        type="text"
        v-model="title"
        data-test="asset-editor-title"
        @keyup.right.stop
        @keyup.left.stop
      />
    </div>
    <div class="mb-4 flex flex-wrap gap-x-8 gap-y-2">
      <div class="tb-form-group mb-0 flex-grow">
        <label class="tb-label">Round</label>

        <select
          class="tb-select"
          data-test="asset-editor-fillType"
          v-model="currentRoundIndex"
        >
          <option
            v-for="(round, index) in rounds"
            :key="`round-${index}`"
            :value="index"
          >
            {{ round.title }}
          </option>
        </select>
      </div>

      <div class="min-w-[180px]">
        <label class="tb-label">Display State</label>
        <div
          class="poll-state"
        >
          <label
            v-for="state in pollStates"
            :key="state"
            class="poll-state__label"
            :class="{ 'poll-state__label--active': currentDisplayState === state }"
          >
            <input
              v-model="currentDisplayState"
              type="radio"
              :value="state"
              class="poll-state__input"
            />
            {{ stateLabels[state] || state }}
          </label>
        </div>
      </div>

      <div class="min-w-[220px]">
        <label class="tb-label">Question</label>
        <div class="data-list__page-controls data-list__page-controls--no-border flex items-stretch">
          <button
            class="tb-btn tb-btn-contained medium-em"
            :disabled="currentQuestionIndex <= 1"
            data-test="graphics-editor-dynamicList-previousPage"
            @click="currentQuestionIndex--"
          >
            <span class="fas fa-backward" />
          </button>

          <input
            type="number"
            :min="1"
            :max="questions.length"
            v-model.number="currentQuestionIndex"
            class="page-input tb-input mx-4 flex-grow text-center"
            data-test="graphics-editor-dynamicList-pageInput"
          />

          <button
            class="tb-btn tb-btn-contained medium-em"
            data-test="graphics-editor-dynamicList-nextPage"
            :disabled="currentQuestionIndex >= questions.length"
            @click="currentQuestionIndex++"
          >
            <span class="fas fa-forward" />
          </button>
        </div>
      </div>
    </div>


    <template v-if="currentDisplayState === 'results'">
      <div class="tb-checkbox">
        <input
          type="checkbox"
          id="animateResults"
          v-model="animateResults"
          data-test="poll-editor-showAnimateControls"
        />

        <label for="animateResults">Manually show results</label>
      </div>

      <div
        v-if="animateResults"
        class="action-key-container"
      >
        <label class="tb-label">
          Vote Animation Controls
        </label>

        <div class="action-keys">
          <action-key
            v-for="(action, index) in actions"
            :key="index"
            :action="action"
            :production_context="production_context"
            :data-test="`poll-editor-${action.hotkey.toLowerCase()}`"
          />
        </div>
      </div>
    </template>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { debounce } from 'lodash-es';

import editingMixins from '../../mixins/editing-mixins';

import ActionKey from '../panel-editor/hotkeys/ActionKey.vue';

export default {
  name: 'PollEditor',
  mixins: [editingMixins],
  props: {
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Object,
      required: true,
    },
  },
  components: {
    ActionKey,
  },

  data() {
    return {
      actions: [
        {
          hotkey: 'I',
          display: 'Animate In',
          description: 'Use this hotkey to animate results in',
          value: 'animate-results-in',
          type: 'poll',
        },
        {
          hotkey: 'O',
          display: 'Animate Out',
          description: 'Use this hotkey to animate results out',
          value: 'animate-results-out',
          type: 'poll',
        },
      ],
    };
  },

  computed: {
    ...mapState('polls', [
      'lookupTable',
      'isLoadingMap',
    ]),

    title: {
      get() {
        return this.item.title;
      },
      set: debounce(function(value) {
        this.setTimelineTitle({
          timelineId: this.editing?.timeline_id,
          title: value,
        });

        this.updateTimeline();
        this.updatePreviewTimeline();
      }, 500),
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      return this.item && (this.editing.timeline_id === this.currentLayoutIndex);
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      return this.item && (this.currentSampleLayoutIndex === this.editing.timeline_id);
    },

    production_context() {
      return { live: this.isCurrentLayout, preview: this.isCurrentSampleLayout };
    },

    poll() {
      return this.item && this.lookupTable[this.item.pollId];
    },

    display() {
      return this.poll?.displays?.find(display => display.displayId === this.item.displayId);
    },

    /**
     * Whether the full poll record (including the display's settings) has
     * loaded. Timeline items can carry a lightweight partial poll entry
     * (prefilled from search metadata) whose display has no settings, so the
     * editor must not render until the full record arrives.
     */
    isPollLoaded() {
      return !!(this.poll && !this.poll.partial && this.display);
    },

    /**
     * Whether the poll metadata is still being fetched.
     */
    isLoadingPoll() {
      return !!(this.isLoadingMap[this.item.pollId] || this.poll?.partial);
    },

    rounds() {
      return this.poll ? this.poll.rounds : [];
    },

    questions() {
      if (!this.poll?.rounds?.length) {
        return [];
      }

      const round = this.poll.rounds[this.currentRoundIndex];
      return round?.questions || [];
    },

    stateLabels() {
      if (!this.display) {
        return {};
      }

      if (this.display.displayType === 'poll') {
        return {
          preVote: 'Open',
          results: 'Post Vote',
          closed: 'Closed',
        };
      }

      return {
        preVote: 'Open',
        results: 'Results',
        closed: 'Final',
      };
    },

    pollStates() {
      if (!this.display?.settings) {
        return [];
      }

      const sortingArray = ['preVote', 'results', 'closed'];

      const { defaults, displaySettings, popUp, ...rest } = this.display.settings;
      return Object.keys(rest).sort((a, b) => sortingArray.indexOf(a) - sortingArray.indexOf(b));
    },

    pollFields() {
      return {
        ...this.editing,
        ...this.editing.fields,
      };
    },

    currentRoundIndex: {
      get() {
        return this.pollFields.currentRoundIndex || 0;
      },
      set(value) {
        this.setField('currentRoundIndex', value);
      },
    },

    currentDisplayState: {
      get() {
        return this.pollFields.currentDisplayState || 'results';
      },
      set(value) {
        this.setField('currentDisplayState', value);
      },
    },

    currentQuestionIndex: {
      get() {
        const idx = this.pollFields.currentQuestionIndex || 0;
        return idx + 1; // Adjust for 1-based index in UI
      },
      set(value) {
        const idx = value - 1; // Adjust for 0-based index in logic
        this.setField('currentQuestionIndex', idx);
      },
    },

    animateResults: {
      get() {
        return this.pollFields.animateResults || false;
      },
      set(value) {
        this.setField('animateResults', value);
      },
    },
  },

  methods: {
    ...mapActions([
      'setTimelineTitle',
      'updateTimeline',
      'updatePreviewTimeline',
    ]),
  },
};
</script>

<style scoped lang="scss">
.poll-editor-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  padding: 1.5rem;
  text-align: center;
  color: variables.$color-on-surface-medium;

  &__message {
    margin: 0;
  }
}

.poll-state {
  &__label {
    padding: 0.5rem 1rem;
    cursor: pointer;
    color: variables.$color-on-surface-high;
    border: 1px solid rgba(255, 255, 255, 0.10);
    border-radius: 0;
    background-color: variables.$color-12dp;

    &:first-child {
      border-top-left-radius: 0.375rem;
      border-bottom-left-radius: 0.375rem;
    }

    &:last-child {
      border-top-right-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
    }

    &--active {
      background-color: rgba(255, 255, 255, 0.25);
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
  }
}
</style>
